// Phase 43 — <CanvasFormSections /> (Plan 43-05 Task 2 + Plan 43-09 gap
// closure, CANVAS-01 / CANVAS-04).
// Re-expresses the 5-step wizard's field layout as 9 flat scroll sections.
// Each editable field is wrapped in <FieldTintWrapper fieldName>, the label
// row carries a <ProvenanceChip>, and conflict-state fields render a
// <ConflictChooser> above the input (D-07).
//
// D-15 contract: this file imports types + hooks ONLY from boat-form/.
// D-15 (V1 era): the wizard folder was never modified by the canvas work.
//
// Phase 43-09 (gap closure): Photos / Engines / Equipment / Listing-
// description sections previously rendered placeholder copy ("wired during
// Plan 43-07 flag-routing integration"). The four real widgets now live as
// canvas-local files under ./sections/ — they consume react-hook-form's
// useFieldArray, the existing useUnifiedMediaUpload handle threaded through
// LiveFillCanvas, and the existing useWizardAI handle for the description
// regenerate trigger. D-15 still holds because every widget is canvas-local;
// the wizard folder was never modified by the canvas work.
//
// Typography lock (UI-SPEC rev 2): 13px (`text-[0.8125rem]`) labels at
// weight 400, 14px body inputs at weight 400, 16px section headings at
// weight 600. The 12px size and 500-weight utility class names are
// disallowed by UI-SPEC and appear nowhere in this file or in any
// canvas/sections/ file.

import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Ship,
  Ruler,
  Cog,
  Truck,
  Package,
  Eye,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@marine/ui/card';
import { Input, InputAddon, InputGroup } from '@marine/ui/input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@marine/ui/form';
import { ToggleGroup, ToggleGroupItem } from '@marine/ui/toggle-group';
import { cn } from '@/lib/utils';
import type {
  ProvenanceEntry,
  BoatFormData,
} from '@/modules/inventory/types/boat.types';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';
import type { useWizardAI } from '@/modules/inventory/components/boat-form/hooks/useWizardAI';
import type { DealershipSettings } from '@/modules/crm/hooks/useDealershipSettings';
import type { UseMarketBandReturn } from '@/modules/inventory/hooks/useMarketBand';
import { useStatuses } from '@/modules/inventory/hooks/useStatuses';
import { useFieldOptions } from '@/modules/core/hooks/useFieldOptions';
import { SyncfusionEditor } from '@/components/editor/SyncfusionEditor';
import { FieldTintWrapper } from './FieldTintWrapper';
import { ProvenanceChip } from './ProvenanceChip';
import { ConflictChooser } from './ConflictChooser';
import { FieldNotices } from './CanvasReview';
import { HinDuplicateNotice } from './HinDuplicateNotice';
import { useProvenance } from './hooks/useProvenance';
import { useHinDuplicateCheck } from './hooks/useHinDuplicateCheck';
import { sortByAuthority } from './lib/sourceAuthority';
import { CanvasSelectField } from './fields/CanvasSelectField';
import { CanvasMultiSelectField } from './fields/CanvasMultiSelectField';
import { CanvasComboboxField } from './fields/CanvasComboboxField';
import { CanvasLocationField } from './fields/CanvasLocationField';
import { CanvasEnginesSection } from './sections/CanvasEnginesSection';
import { CanvasEquipmentSection } from './sections/CanvasEquipmentSection';
import { CanvasListingDescriptionSection } from './sections/CanvasListingDescriptionSection';
// Phase 44-07 — PRICE-02 · D-07 · D-09 · D-11 — pricing section now owns
// the market-band "Suggest price" + SuggestedPriceChip + ProvenanceChip
// mutual-exclusion matrix on the `our_price` field. Extracted out of the
// inline SectionPricing below into its own file.
import { PricingSection as CanvasPricingSection } from './sections/PricingSection';

// =============================================================================
// FieldRow — shared building block for every editable field on the canvas.
// Wraps the input in a tint wrapper, hangs a provenance chip on the label
// row, and conditionally renders a conflict chooser above the input.
// =============================================================================

type WizardFieldName = keyof WizardFormData;

interface FieldRowProps {
  fieldName: WizardFieldName;
  label: string;
  /**
   * #787 — 'editor' renders the restricted SyncfusionEditor instead of an
   * <Input>, keeping every other FieldRow affordance (tint wrapper,
   * provenance chip, conflict chooser, validation message) identical.
   */
  type?: 'text' | 'number' | 'editor';
  placeholder?: string;
  required?: boolean;
  /**
   * Phase 45.5.3 (CANVAS-14) — when set on a number field, the input is wrapped
   * in an InputGroup with a trailing InputAddon showing the unit (e.g. ft / in /
   * HP / hrs), matching v1 SpecsAndConfigStep.tsx:203-219. Number-only — the
   * editor variant ignores it.
   */
  unit?: string;
}

// Mirrors the legacy AddBoatWizard field pattern (FormField + FormItem +
// FormLabel + FormControl + FormMessage from @marine/ui/form) so the
// canvas inputs render identically to every other form in the app —
// including the same label sizing (text-xs), required asterisk, and
// validation-message line. Canvas-specific extras (FieldTintWrapper,
// ProvenanceChip, ConflictChooser) sit OUTSIDE the FormItem to avoid
// disturbing the FormField layout the rest of the app standardised on.
function FieldRow({
  fieldName,
  label,
  type = 'text',
  placeholder,
  required = false,
  unit,
}: FieldRowProps) {
  const { control } = useFormContext<WizardFormData>();
  const { state, clear, selectSource, markEdited } = useProvenance(fieldName);

  // Phase 45.5 (D-13) — editing ANY AI-filled field is the ONLY accept gesture:
  // it flips the field to user-entered (chip → "✓ Edited by you", border drops)
  // and the edit survives a re-extract. markEdited is a no-op for empty/manual
  // fields and idempotent once already edited.
  const handleEdited = () => markEdited();

  return (
    <FieldTintWrapper fieldName={fieldName}>
      <FormField
        control={control}
        name={fieldName as never}
        render={({ field }) => {
          // #787 — editor variant: the restricted SyncfusionEditor replaces
          // the <Input>. onChange MUST keep firing handleEdited — markEdited
          // is the D-13 provenance accept gesture and must not regress.
          const editorEl = (
            <SyncfusionEditor
              value={field.value ?? ''}
              onChange={(v: string) => {
                field.onChange(v);
                handleEdited();
              }}
              preset="restricted"
              ariaLabel={label}
              placeholder={placeholder}
              height={150}
            />
          );

          // Phase 45.5.3 (CANVAS-14) — the input element, shared between the
          // bare and the unit-addon paths. KEEP the numeric onChange coercion +
          // markEdited (do NOT regress provenance) regardless of wrapping.
          const inputEl = (
            <Input
              type={type === 'editor' ? 'text' : type}
              placeholder={placeholder}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                if (type === 'number') {
                  const v = e.target.value;
                  field.onChange(v === '' ? null : Number(v));
                } else {
                  field.onChange(e);
                }
                handleEdited();
              }}
            />
          );

          return (
            <FormItem>
              <div className="flex items-center justify-between gap-2">
                <FormLabel className="text-xs">
                  {label}
                  {required && (
                    <span className="text-destructive ml-0.5">*</span>
                  )}
                </FormLabel>
                <ProvenanceChip
                  fieldName={fieldName}
                  entry={state.entry}
                  onClear={clear}
                  isLowConfidence={state.isLowConfidence}
                />
              </div>
              {state.isConflict && Array.isArray(state.entry) && (
                <ConflictChooser
                  fieldName={fieldName}
                  variant="radio"
                  // Authority order COO > invoice > BOL > web (D-13). Sort the
                  // candidate values so the highest-authority source is first.
                  entries={sortByAuthority(state.entry as ProvenanceEntry[])}
                  selectedIndex={state.pickedIndex}
                  onSelectOne={(entry) => selectSource(entry)}
                />
              )}
              <FieldNotices fieldName={fieldName} />
              <FormControl>
                {type === 'editor' ? (
                  editorEl
                ) : unit && type === 'number' ? (
                  <InputGroup>
                    {inputEl}
                    <InputAddon>{unit}</InputAddon>
                  </InputGroup>
                ) : (
                  inputEl
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </FieldTintWrapper>
  );
}

// Phase 45.5.3 (CANVAS-14) — sortByAuthority + SOURCE_AUTHORITY were lifted to
// ./lib/sourceAuthority so CanvasSelectField reuses the SAME map. Imported above.

// Phase 45.5 (D-02) — a US boat HIN is exactly 12 chars: 3-char MIC + 5-char
// serial + 4-char date. The HIN-verified emerald chip shows when this passes.
const HIN_REGEX = /^[A-Z]{3}[A-Z0-9]{9}$/;

// =============================================================================
// HinFieldRow — Phase 45.5 (D-02 / D-18). The standard FieldRow PLUS the
// HIN-verified emerald chip (when the 12-char cross-check passes) and the slim
// HIN-duplicate notice (when the HIN already exists in inventory).
// =============================================================================
function HinFieldRow({ boatId }: { boatId: string | null }) {
  // IN-02: resolve the form context once (was called twice in this component).
  const { control, watch } = useFormContext<WizardFormData>();
  const fieldName = 'hin_number' as WizardFieldName;
  const { state, clear, selectSource, markEdited } = useProvenance(fieldName);
  const hinValue = (watch(fieldName) as string) ?? '';
  const isHinVerified = HIN_REGEX.test(hinValue.trim().toUpperCase());
  const hinDup = useHinDuplicateCheck(boatId);

  return (
    <FieldTintWrapper fieldName={fieldName}>
      <FormField
        control={control}
        name={fieldName as never}
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between gap-2">
              <FormLabel className="text-xs">HIN</FormLabel>
              <ProvenanceChip
                fieldName={fieldName}
                entry={state.entry}
                onClear={clear}
                isLowConfidence={state.isLowConfidence}
                isHinVerified={isHinVerified}
              />
            </div>
            {state.isConflict && Array.isArray(state.entry) && (
              <ConflictChooser
                fieldName={fieldName}
                variant="radio"
                entries={sortByAuthority(state.entry as ProvenanceEntry[])}
                selectedIndex={state.pickedIndex}
                onSelectOne={(entry) => selectSource(entry)}
              />
            )}
            <FieldNotices fieldName={fieldName} />
            <FormControl>
              <Input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e);
                  markEdited();
                }}
              />
            </FormControl>
            {/* D-18 — slim inline duplicate notice (NOT a modal). */}
            <HinDuplicateNotice
              show={hinDup.show}
              existingBoatId={hinDup.existingBoatId}
              onViewExisting={(id) =>
                // Open the existing boat in a NEW tab so the dealer doesn't lose
                // the in-progress canvas they're filling for the duplicate.
                window.open(`/inventory/boats/${id}`, '_blank', 'noopener,noreferrer')
              }
              onUseAnyway={hinDup.dismiss}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    </FieldTintWrapper>
  );
}

// =============================================================================
// SectionHeading — shared 16px semibold heading per UI-SPEC §Typography.
// =============================================================================

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-base font-semibold">{children}</h2>;
}

// =============================================================================
// SectionCard — Phase 45.5.3 (CANVAS-14). Wraps a form section in v1's Card
// pattern (icon + CardTitle header), mirroring SpecsAndConfigStep.tsx:186-192.
//
// CRITICAL scroll-spy invariant (RESEARCH Pitfall 1): the `id={sectionId}` stays
// on the outermost <section> that remains in the DOM, because the
// IntersectionObserver in useCanvasNav targets getElementById(id) for every
// CANVAS_SECTION_IDS entry. Wrapping the body in a Card MUST NOT move or drop
// any of: identity / specs / engines / trailer / equipment / pricing / listing /
// review.
// =============================================================================

type SectionCardIcon = React.ComponentType<{ className?: string }>;

function SectionCard({
  id,
  title,
  Icon,
  required = false,
  children,
}: {
  id: string;
  title: string;
  Icon: SectionCardIcon;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <section id={id}>
      <Card>
        <CardHeader className="min-h-[34px] bg-accent/50">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            <CardTitle className="text-2sm">
              {title}
              {required && <span className="text-destructive ml-0.5">*</span>}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">{children}</CardContent>
      </Card>
    </section>
  );
}

// =============================================================================
// PlaceholderNote — used in sections whose primary widget (photo gallery,
// engines field-array, streaming description) lives inside the wizard and is
// wired by the host page during Plan 43-07's flag-routing integration. We
// keep the section's <h2> heading + a 13px font-normal placeholder line so
// IntersectionObserver scroll-spy still has a visible target.
// =============================================================================

function PlaceholderNote({ children }: { children: React.ReactNode }) {
  return (
    <p className={cn('text-[0.8125rem] font-normal text-muted-foreground')}>
      {children}
    </p>
  );
}

// =============================================================================
// 9 sections (D-01 — flat re-expression of the 5-step wizard).
// =============================================================================

// ConditionRow — Identity section's first field. Mirrors the legacy
// BoatAndPhotosStep ToggleGroup pattern verbatim (single-select, outline
// variant, full-width with each item flex-1) so dealers see the same
// New / Used affordance the Sheet wizard has.
function ConditionRow() {
  const { control } = useFormContext<WizardFormData>();
  return (
    <FieldTintWrapper fieldName={'condition' as WizardFieldName}>
      <FormField
        control={control}
        name={'condition' as never}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs">
              Condition
              <span className="text-destructive ml-0.5">*</span>
            </FormLabel>
            <FieldNotices fieldName="condition" />
            <FormControl>
              <ToggleGroup
                type="single"
                variant="outline"
                value={field.value as string | undefined}
                onValueChange={(value) => {
                  // Guard against deselection: Radix fires '' on click-active.
                  if (value) field.onChange(value);
                }}
                className="w-full"
              >
                <ToggleGroupItem value="new" className="flex-1 whitespace-nowrap">
                  New
                </ToggleGroupItem>
                <ToggleGroupItem value="used" className="flex-1 whitespace-nowrap">
                  Used
                </ToggleGroupItem>
              </ToggleGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </FieldTintWrapper>
  );
}

function SectionIdentity({ boatId }: { boatId: string | null }) {
  return (
    <SectionCard id="identity" title="Identity" Icon={Ship}>
      {/* Full-width spanner: New/Used toggle (D-3). */}
      <ConditionRow />
      {/* CANVAS-09 (D-3) — scalar identity fields in a responsive 2-col grid. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
        <CanvasComboboxField fieldName="manufacturer" label="Manufacturer" required placeholder="e.g. Boston Whaler" />
        <CanvasComboboxField fieldName="model" label="Model" required placeholder="Select or type a model" />
        <FieldRow fieldName="model_year" label="Year" type="number" required />
      </div>
      {/* Phase 45.5 (D-02/D-18) — HIN row with the verified chip + dup notice.
          Full-width spanner: HIN input + HinDuplicateNotice (D-3). */}
      <HinFieldRow boatId={boatId} />
    </SectionCard>
  );
}

// Phase 45.5.2 (CANVAS-10) — the separate Photos UPLOAD section was REMOVED so
// the hero dropzone (CanvasHeroIngest + UploadedFilesStrip) is the single
// unified upload entry point for images AND documents. The committed media is
// surfaced in the hero's inline strip; no second upload affordance lives here.
// Public/private routing stays server-authoritative — no client bucket decision
// was added by this removal. CanvasPhotosSection.tsx is preserved on disk
// (deferred per CONTEXT D-09 + CLAUDE.md — removal is render+wiring only).

function SectionSpecs() {
  const fuelTypeOptions = useFieldOptions("fuel_type");
  const boatTypeOptions = useFieldOptions("boat_type");
  return (
    <SectionCard id="specs" title="Specs" Icon={Ruler}>
      {/* CANVAS-09 (D-3) — all scalar spec fields in a responsive 2-col grid.
          CANVAS-14 — Length/Beam/Draft carry ft/in/in unit addons; Fuel type is
          a Select. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
        <div className="sm:col-span-2">
          <CanvasMultiSelectField
            fieldName="boat_types"
            label="Boat types"
            options={boatTypeOptions.options}
            loading={boatTypeOptions.isLoading}
            placeholder="Select boat types"
          />
        </div>
        <FieldRow fieldName="length_ft" label="Length" type="number" unit="ft" />
        <FieldRow fieldName="beam_in" label="Beam" type="number" unit="in" />
        <FieldRow fieldName="draft_in" label="Draft" type="number" unit="in" />
        <FieldRow fieldName="seating_capacity" label="Seating capacity" type="number" />
        <FieldRow fieldName="exterior_color" label="Exterior color" />
        <FieldRow fieldName="interior_color" label="Interior color" />
        <CanvasSelectField
          fieldName="fuel_type"
          label="Fuel type"
          options={fuelTypeOptions.options}
          loading={fuelTypeOptions.isLoading}
          placeholder="Select fuel type"
        />
      </div>
    </SectionCard>
  );
}

function SectionEngines() {
  // Engines is a field-array (engines[]). Phase 43-09 gap closure: real
  // CanvasEnginesSection widget (canvas-local useFieldArray) replaces the
  // earlier deferred-to-Plan-43-07 placeholder. engine_type stays as a
  // top-level FieldRow because it is a single string field on the form,
  // not part of the per-engine InventoryEngine subobject.
  const engineTypeOptions = useFieldOptions("engine_type");
  return (
    <SectionCard id="engines" title="Engines" Icon={Cog}>
      {/* engine_type is a single top-level Select (CANVAS-14), NOT per-engine. */}
      <CanvasSelectField
        fieldName="engine_type"
        label="Engine type"
        options={engineTypeOptions.options}
        loading={engineTypeOptions.isLoading}
        placeholder="Select engine type"
      />
      <CanvasEnginesSection />
    </SectionCard>
  );
}

function SectionTrailer() {
  const axleOptions = useFieldOptions("trailer_axles");
  return (
    <SectionCard id="trailer" title="Trailer" Icon={Truck}>
      {/* CANVAS-09 (D-3) — all scalar trailer fields in a responsive 2-col grid. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
        <FieldRow fieldName="trailer_make" label="Trailer make" />
        <FieldRow fieldName="trailer_model" label="Trailer model" />
        <FieldRow fieldName="trailer_vin" label="Trailer VIN" />
        <CanvasSelectField
          fieldName="trailer_axles"
          label="Trailer axles"
          options={axleOptions.options}
          loading={axleOptions.isLoading}
          placeholder="Select axles"
        />
      </div>
    </SectionCard>
  );
}

function SectionEquipment() {
  // Phase 43-09 gap closure: real CanvasEquipmentSection widget
  // (canvas-local useFieldArray for both standard_equipment and
  // optional_equipment) replaces the earlier deferred-to-Plan-43-07
  // placeholder. Per-row provenance / per-tag chips are out of scope —
  // Phase 47 Photo AI ships that surface separately.
  return (
    <SectionCard id="equipment" title="Equipment" Icon={Package}>
      <CanvasEquipmentSection />
    </SectionCard>
  );
}

// Phase 44-07 — the pricing section extracted into sections/PricingSection.tsx
// so it can own the market-band "Suggest price" / SuggestedPriceChip /
// ProvenanceChip mutual-exclusion matrix on `our_price`. Re-declared here as
// a local alias for readability; the actual implementation lives in the
// sections/ file.
// Phase 44 carryover Task 2: marketBand is threaded through from the parent
// (CanvasFormSections → SectionPricing → PricingSection → OurPriceRow).
function SectionPricing({ boatId, marketBand }: { boatId: string | null; marketBand: UseMarketBandReturn }) {
  return <CanvasPricingSection boatId={boatId} marketBand={marketBand} />;
}

function SectionListing({
  wizardAI,
}: {
  wizardAI: ReturnType<typeof useWizardAI> | undefined;
}) {
  // Phase 43-09 gap closure: real CanvasListingDescriptionSection widget
  // (long-form textarea + Regenerate AiActionButton wired to
  // wizardAI.description.trigger.mutate()) replaces the earlier deferred-
  // to-Plan-43-07 placeholder. The streaming preview slice of
  // AIDescriptionCard remains out of scope (one-shot mutate is enough
  // for gap closure per Plan 43-09).
  // CANVAS-14 — Status is a Select fed by useStatuses (filter active + non-empty
  // id, map to {value,label}); the inline spinner shows while the fetch is in
  // flight. Location is the v1 multi-select checkbox grid (CanvasLocationField),
  // replacing the old single primary_location_id text input.
  const { data: statusesResponse, isLoading: statusesLoading } = useStatuses();
  const statusOptions = (statusesResponse?.items ?? [])
    .filter((s) => s.id && s.is_active !== false)
    .map((s) => ({ value: s.id, label: s.label }));

  return (
    <SectionCard id="listing" title="Listing" Icon={Eye}>
      {/* CANVAS-09 (D-3) — scalar listing fields in a responsive 2-col grid.
          CANVAS-14 — Status is now a Select. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
        <CanvasSelectField
          fieldName="status_id"
          label="Status"
          options={statusOptions}
          loading={statusesLoading}
          placeholder="Select status"
        />
      </div>
      {/* CANVAS-14 (D-1) — multi-location checkbox grid + ⭐ primary (full width). */}
      <CanvasLocationField />
      {/* Full-width spanner: descriptions span the full width (D-3).
          #787 — short_description uses the FieldRow editor variant (restricted
          rich text) so wizard-authored formatting round-trips the sanitizer. */}
      <FieldRow fieldName="short_description" label="Short description" type="editor" />
      <CanvasListingDescriptionSection wizardAI={wizardAI} />
    </SectionCard>
  );
}

function SectionReview({
  wizardAI: _wizardAI,
}: {
  wizardAI: ReturnType<typeof useWizardAI> | undefined;
}) {
  // Review is read-only — a summary scan of the assembled draft.
  const { getValues } = useFormContext<WizardFormData>();
  const values = getValues();
  // `_provenance` is attached to form values at runtime by the canvas hydration
  // path (see LiveFillCanvas), but is intentionally not in WizardFormData.
  const filled = (Object.keys(values) as Array<keyof WizardFormData>).filter(
    (k) => {
      if ((k as string) === '_provenance') return false;
      const v = values[k];
      if (v === null || v === undefined || v === '') return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    },
  ).length;
  // IN-03: count symmetrically with `filled` (which also filters _provenance).
  // A blank-new form that has never hydrated may not carry the _provenance key,
  // so the old `length - 1` under-counted by one in that case.
  const total = Object.keys(values).filter((k) => k !== '_provenance').length;
  return (
    <section id="review" className="space-y-4">
      <SectionHeading>Review</SectionHeading>
      <PlaceholderNote>
        {filled} of {total} fields filled. Use the Create boat button below to
        publish, or keep editing — autosave keeps your draft current.
      </PlaceholderNote>
    </section>
  );
}

// =============================================================================
// CanvasFormSections — composer.
// Spacing scale per UI-SPEC: lg (24px) inside a section between field rows;
// 2xl (~48px) between sections — Tailwind `space-y-12` is 48px.
// =============================================================================

interface CanvasFormSectionsProps {
  wizardAI?: ReturnType<typeof useWizardAI>;
  dealershipSettings?: DealershipSettings;
  sectionIds?: readonly string[];
  /**
   * Phase 44-07 — persisted inventory_item_id threaded through to
   * SectionPricing → PricingSection. When null, the "Suggest price" button
   * renders disabled with the "Save draft first" tooltip (D-07 + Pitfall #8).
   */
  boatId?: string | null;
  /**
   * Phase 44 carryover Task 2 — the shared useMarketBand instance lifted to
   * LiveFillCanvas. Required so EXACTLY ONE call site exists across the canvas
   * (the CTA-triggered run and the explicit "Suggest price" button share the
   * same job/result state). A missing value is a type error, not a silent
   * undefined — callers MUST pass this (tests can supply a stub).
   */
  marketBand: UseMarketBandReturn;
}

export function CanvasFormSections({
  wizardAI,
  dealershipSettings: _dealershipSettings,
  boatId = null,
  marketBand,
}: CanvasFormSectionsProps) {
  // BoatFormData reference kept for type-completion clarity at the file
  // boundary — the FieldRow renders against the WizardFormData superset.
  void ({} as Partial<BoatFormData>);
  // Phase 43-09: dealershipSettings is no longer consumed inside this
  // composer (SectionListing previously took it as an unused prop and now
  // takes wizardAI instead). Kept on the prop interface so LiveFillCanvas
  // can keep passing it without a coordinated refactor; underscore-prefixed
  // here so the lint rule does not flag the unused destructured local.
  return (
    <div className="space-y-12">
      <SectionIdentity boatId={boatId} />
      <SectionSpecs />
      <SectionEngines />
      <SectionTrailer />
      <SectionEquipment />
      <SectionPricing boatId={boatId} marketBand={marketBand} />
      <SectionListing wizardAI={wizardAI} />
      <SectionReview wizardAI={wizardAI} />
    </div>
  );
}
